import React, { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Navn er påkrævet';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer er påkrævet';
    } else if (!/^\+?[0-9\s-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig e-mailadresse';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Besked er påkrævet';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // Honeypot field
        })
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Tak for din besked! Vi vender tilbage til dig hurtigst muligt.'
        });
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv igen.');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Der skete en fejl under afsendelsen. Prøv igen.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-8 md:p-10 border border-[--color-brand-sand] shadow-xs">
      <h3 className="font-display text-2xl md:text-3xl text-[--color-brand-charcoal] font-bold mb-2">
        Send os en besked
      </h3>
      <p className="font-sans text-sm text-[--color-brand-muted] mb-8">
        Udfyld formularen, så vender vi tilbage til dig med et uforpligtende tilbud på din opgave.
      </p>

      {status.type === 'success' ? (
        <div className="bg-[--color-brand-sage-pale]/30 border border-[--color-brand-green-light]/50 rounded-2xl p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-[--color-brand-green-dark] text-white flex items-center justify-center mx-auto mb-4">
            <i className="fa-solid fa-check text-lg"></i>
          </div>
          <h4 className="font-display text-lg font-bold text-[--color-brand-green-dark] mb-2">
            Beskeden er sendt!
          </h4>
          <p className="font-sans text-sm text-[--color-brand-muted]">
            {status.message}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute pointer-events-none" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block font-sans text-xs font-bold uppercase tracking-wider text-[--color-brand-charcoal] mb-2">
              Navn <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-5 py-3.5 rounded-2xl border bg-[--color-brand-cream]/30 font-sans text-sm text-[--color-brand-charcoal] focus:outline-none focus:ring-2 focus:ring-[--color-brand-green-dark] transition-all ${
                errors.name ? 'border-red-400 focus:ring-red-400' : 'border-[--color-brand-sand]'
              }`}
              placeholder="Dit fulde navn"
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1.5 font-sans">{errors.name}</p>
            )}
          </div>

          {/* Grid for Phone and Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block font-sans text-xs font-bold uppercase tracking-wider text-[--color-brand-charcoal] mb-2">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-5 py-3.5 rounded-2xl border bg-[--color-brand-cream]/30 font-sans text-sm text-[--color-brand-charcoal] focus:outline-none focus:ring-2 focus:ring-[--color-brand-green-dark] transition-all ${
                  errors.phone ? 'border-red-400 focus:ring-red-400' : 'border-[--color-brand-sand]'
                }`}
                placeholder="F.eks. 30 44 16 11"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1.5 font-sans">{errors.phone}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block font-sans text-xs font-bold uppercase tracking-wider text-[--color-brand-charcoal] mb-2">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-5 py-3.5 rounded-2xl border bg-[--color-brand-cream]/30 font-sans text-sm text-[--color-brand-charcoal] focus:outline-none focus:ring-2 focus:ring-[--color-brand-green-dark] transition-all ${
                  errors.email ? 'border-red-400 focus:ring-red-400' : 'border-[--color-brand-sand]'
                }`}
                placeholder="din@email.dk"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1.5 font-sans">{errors.email}</p>
              )}
            </div>
          </div>

          {/* Message Field */}
          <div>
            <label htmlFor="message" className="block font-sans text-xs font-bold uppercase tracking-wider text-[--color-brand-charcoal] mb-2">
              Beskrivelse <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows="5"
              value={formData.message}
              onChange={handleChange}
              className={`w-full px-5 py-3.5 rounded-2xl border bg-[--color-brand-cream]/30 font-sans text-sm text-[--color-brand-charcoal] focus:outline-none focus:ring-2 focus:ring-[--color-brand-green-dark] transition-all resize-none ${
                errors.message ? 'border-red-400 focus:ring-red-400' : 'border-[--color-brand-sand]'
              }`}
              placeholder="Beskriv din opgave her..."
            />
            {errors.message && (
              <p className="text-red-500 text-xs mt-1.5 font-sans">{errors.message}</p>
            )}
          </div>

          {/* Error Status */}
          {status.type === 'error' && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm font-sans flex items-center gap-3">
              <i className="fa-solid fa-circle-exclamation text-base flex-shrink-0"></i>
              <p>{status.message}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#BDC8B3] text-[--color-brand-green-dark] hover:bg-[--color-brand-green-dark] hover:text-white disabled:bg-[--color-brand-green-light] disabled:cursor-not-allowed transition-all duration-200 text-sm font-bold tracking-wider uppercase shadow-sm cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner animate-spin mr-2"></i>
                Sender besked...
              </>
            ) : (
              <>
                Send besked
                <i className="fa-solid fa-paper-plane ml-2 text-xs"></i>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
